/**
 * DailyStatusReport — one-page recruiter productivity report.
 *
 * Backed by GET /api/reports/dsr — an aggregate of recruiter_activities,
 * candidate_followups, audit_events (stage changes), interviews and offers
 * clustered into REAL PROGRESS vs BUSY WORK. Same data, downloadable as a
 * PDF via /api/reports/dsr/pdf. Auto-emails to the employer at 18:15 IST
 * daily (see routers/dsr.py `dsr_email_loop`).
 */
import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Calendar as CalendarIcon, Download, Loader2, TrendingUp, Zap, Send, User } from "lucide-react";

function todayIso() {
    const d = new Date();
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
}

function pctBar(pct) {
    const p = Math.max(0, Math.min(100, pct));
    return (
        <div className="h-2 w-full bg-neutral-200 overflow-hidden">
            <div className="h-full bg-emerald-500 transition-[width] duration-300" style={{ width: `${p}%` }} />
        </div>
    );
}

function verdict(pct, totalActions) {
    if (totalActions === 0) return { label: "No activity", cls: "border-slate-300 text-slate-500 bg-slate-50" };
    if (pct >= 60) return { label: "High-impact day", cls: "border-emerald-500 text-emerald-700 bg-emerald-50" };
    if (pct >= 30) return { label: "Mixed — check follow-ups", cls: "border-amber-500 text-amber-700 bg-amber-50" };
    return { label: "Mostly busy work", cls: "border-rose-400 text-rose-700 bg-rose-50" };
}

const PROGRESS_METRIC_LABELS = {
    applications_advanced: "Applications advanced",
    interviews_scheduled: "Interviews scheduled",
    interviews_completed: "Interviews completed",
    offers_created: "Offers created",
    hires_closed: "Hires closed",
    positive_followups: "Positive follow-ups",
};

const BUSY_METRIC_LABELS = {
    unreachable_followups: "Unreachable follow-ups",
    neutral_followups: "Neutral follow-ups",
    stage_regressions: "Stage regressions",
    candidates_added_no_move: "Candidates added — no move",
    notes_only: "Notes without stage move",
    ai_runs_without_move: "AI runs without move",
};

export default function DailyStatusReport() {
    const [date, setDate] = useState(todayIso());
    const [recruiterId, setRecruiterId] = useState("");
    const [dsr, setDsr] = useState(null);
    const [loading, setLoading] = useState(false);
    const [sendingNow, setSendingNow] = useState(false);
    const [err, setErr] = useState(null);

    async function load() {
        setLoading(true); setErr(null);
        try {
            const params = new URLSearchParams({ date });
            if (recruiterId) params.set("recruiter_id", recruiterId);
            const { data } = await api.get(`/reports/dsr?${params.toString()}`);
            setDsr(data);
        } catch (e) {
            setErr(formatApiError(e));
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => { load(); }, [date, recruiterId]); // load re-runs on filter change

    const recruiters = dsr?.recruiters || [];
    const totals = dsr?.totals || {};

    // Recruiter dropdown options — from the loaded DSR (so we don't need a
    // separate /users endpoint; the DSR already lists all in-scope users).
    const recOptions = useMemo(() => {
        return recruiters.map((r) => ({ id: r.recruiter_id, name: r.recruiter_name, role: r.recruiter_role }));
    }, [recruiters]);

    function downloadPdf() {
        const params = new URLSearchParams({ date });
        if (recruiterId) params.set("recruiter_id", recruiterId);
        const backend = process.env.REACT_APP_BACKEND_URL;
        // Same-origin blob download — server sets Content-Disposition.
        window.open(`${backend}/api/reports/dsr/pdf?${params.toString()}`, "_blank", "noopener");
    }

    async function sendNow() {
        setSendingNow(true);
        try {
            const { data } = await api.post(`/reports/dsr/send-now?date=${encodeURIComponent(date)}`);
            toast.success(`Emailed ${data.sent} recruiter report${data.sent === 1 ? "" : "s"} to employer(s). ${data.skipped} skipped (no activity).`);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setSendingNow(false);
        }
    }

    return (
        <div className="max-w-7xl mx-auto p-4 sm:p-6 space-y-5" data-testid="dsr-page">
            {/* Header */}
            <header className="flex flex-wrap items-end gap-3 border-b border-border pb-4">
                <div className="flex-1 min-w-[240px]">
                    <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Reports</div>
                    <h1 className="font-display font-black text-3xl leading-tight text-ikb">Daily Status Report</h1>
                    <p className="text-xs text-muted-foreground mt-1">
                        Real progress vs. busy work per recruiter. Auto-emailed to employers daily at 18:15 IST.
                    </p>
                </div>
                <label className="block">
                    <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Date</span>
                    <div className="mt-1 flex items-center gap-1">
                        <CalendarIcon className="w-4 h-4 text-muted-foreground" />
                        <input
                            type="date"
                            value={date}
                            onChange={(e) => setDate(e.target.value)}
                            className="h-9 border border-border bg-white text-sm px-2"
                            data-testid="dsr-date-picker"
                        />
                    </div>
                </label>
                <label className="block">
                    <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Recruiter</span>
                    <select
                        value={recruiterId}
                        onChange={(e) => setRecruiterId(e.target.value)}
                        className="mt-1 h-9 border border-border bg-white text-sm px-2 min-w-[200px]"
                        data-testid="dsr-recruiter-filter"
                    >
                        <option value="">All recruiters</option>
                        {recOptions.map((r) => (
                            <option key={r.id} value={r.id}>{r.name} ({r.role})</option>
                        ))}
                    </select>
                </label>
                <button
                    type="button"
                    onClick={downloadPdf}
                    className="h-9 px-3 border border-ikb bg-ikb text-white text-xs font-mono uppercase tracking-widest hover:bg-ikb/90 inline-flex items-center gap-1.5"
                    data-testid="dsr-download-pdf"
                >
                    <Download className="w-3.5 h-3.5" /> Export PDF
                </button>
                <button
                    type="button"
                    onClick={sendNow}
                    disabled={sendingNow}
                    className="h-9 px-3 border border-emerald-600 text-emerald-700 bg-white text-xs font-mono uppercase tracking-widest hover:bg-emerald-50 inline-flex items-center gap-1.5 disabled:opacity-60"
                    data-testid="dsr-send-now"
                    title="Send today's DSR to employers now (skips the 18:15 wait)"
                >
                    {sendingNow ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                    Email now
                </button>
            </header>

            {loading && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Loader2 className="w-4 h-4 animate-spin" /> Aggregating today&apos;s activity…
                </div>
            )}
            {err && (
                <div className="border border-red-300 bg-red-50 text-red-800 p-3 text-xs" data-testid="dsr-error">{err}</div>
            )}

            {dsr && !loading && (
                <>
                    {/* Company-wide headline */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <div className="border border-border bg-white p-4">
                            <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-emerald-700">
                                <TrendingUp className="w-3.5 h-3.5" /> Real progress
                            </div>
                            <div className="font-display font-black text-4xl tabular-nums mt-1 text-emerald-700" data-testid="dsr-total-progress">
                                {totals.total_progress || 0}
                            </div>
                        </div>
                        <div className="border border-border bg-white p-4">
                            <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-amber-700">
                                <Zap className="w-3.5 h-3.5" /> Busy work
                            </div>
                            <div className="font-display font-black text-4xl tabular-nums mt-1 text-amber-700" data-testid="dsr-total-busy">
                                {totals.total_busy || 0}
                            </div>
                        </div>
                        <div className="border border-border bg-white p-4">
                            <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">% Real progress</div>
                            <div className="font-display font-black text-4xl tabular-nums mt-1 text-ikb" data-testid="dsr-progress-pct">
                                {totals.progress_pct || 0}%
                            </div>
                            <div className="mt-2">{pctBar(totals.progress_pct || 0)}</div>
                        </div>
                    </div>

                    {/* Per-recruiter cards */}
                    <div className="grid grid-cols-1 gap-4">
                        {recruiters.length === 0 ? (
                            <div className="text-sm text-muted-foreground border border-dashed border-border p-6 text-center">
                                No recruiters found for the selected filter.
                            </div>
                        ) : recruiters.map((r) => {
                            const v = verdict(r.progress_pct, r.total_actions);
                            return (
                                <section
                                    key={r.recruiter_id}
                                    className="border border-border bg-white"
                                    data-testid={`dsr-recruiter-${r.recruiter_id}`}
                                >
                                    <header className="flex items-center gap-3 px-4 py-3 border-b border-border bg-neutral-50">
                                        <div className="w-8 h-8 rounded-full bg-ikb/10 text-ikb inline-flex items-center justify-center">
                                            <User className="w-4 h-4" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="font-semibold text-sm truncate">{r.recruiter_name}</div>
                                            <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
                                                {r.recruiter_role} · {r.recruiter_email}
                                            </div>
                                        </div>
                                        <div className="text-right">
                                            <div className="font-display font-black text-2xl tabular-nums text-ikb">{r.progress_pct}%</div>
                                            <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{r.total_actions} actions</div>
                                        </div>
                                        <span className={`text-[10px] font-mono uppercase tracking-widest px-2 py-1 border ${v.cls}`}>{v.label}</span>
                                    </header>
                                    <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-border">
                                        <div className="p-4">
                                            <div className="text-[10px] font-mono uppercase tracking-widest text-emerald-700 mb-2 flex items-center gap-1.5">
                                                <TrendingUp className="w-3 h-3" /> Real progress
                                            </div>
                                            <ul className="space-y-1.5">
                                                {Object.entries(PROGRESS_METRIC_LABELS).map(([k, label]) => (
                                                    <li key={k} className="flex items-center justify-between text-xs">
                                                        <span className="text-foreground/80">{label}</span>
                                                        <span className={`font-mono tabular-nums ${(r.real_progress?.[k] || 0) > 0 ? "text-emerald-700 font-bold" : "text-foreground/40"}`}>
                                                            {r.real_progress?.[k] || 0}
                                                        </span>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                        <div className="p-4">
                                            <div className="text-[10px] font-mono uppercase tracking-widest text-amber-700 mb-2 flex items-center gap-1.5">
                                                <Zap className="w-3 h-3" /> Busy work
                                            </div>
                                            <ul className="space-y-1.5">
                                                {Object.entries(BUSY_METRIC_LABELS).map(([k, label]) => (
                                                    <li key={k} className="flex items-center justify-between text-xs">
                                                        <span className="text-foreground/80">{label}</span>
                                                        <span className={`font-mono tabular-nums ${(r.busy_work?.[k] || 0) > 0 ? "text-amber-700 font-bold" : "text-foreground/40"}`}>
                                                            {r.busy_work?.[k] || 0}
                                                        </span>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    </div>
                                    {(r.next_actions_due || []).length > 0 && (
                                        <div className="border-t border-border p-3 bg-blue-50/40">
                                            <div className="text-[10px] font-mono uppercase tracking-widest text-blue-700 mb-1">
                                                Next actions due · {r.next_actions_due.length}
                                            </div>
                                            <ul className="flex flex-wrap gap-1.5">
                                                {r.next_actions_due.slice(0, 8).map((n, i) => (
                                                    <li key={i} className="text-[10px] font-mono px-1.5 py-0.5 border border-blue-300 bg-white text-blue-800">
                                                        {n.status || "n/a"} · {new Date(n.next_action_at).toLocaleDateString()}
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                </section>
                            );
                        })}
                    </div>
                </>
            )}
        </div>
    );
}
